/**
 * 변경 이력 "한 번의 저장 = 한 덩어리" 묶음 규칙 (v2.043).
 *
 * 영업건 상세(SalesDetailModal)·매입 수정(PurchaseAuditSection) 두 화면이 각자 같은 함수를
 * 복사해 갖고 있던 것을 여기로 합쳤다. 규칙을 고칠 곳이 한 군데가 되도록.
 *
 * ── 왜 분(分) 단위가 아니라 시간 간격인가 ────────────────────────────
 * 종전 규칙: `changedAt.slice(0,16)`(분) + 사용자 + 동작.
 * 이건 두 방향으로 틀렸다 (운영 10,461 저장 실측, 2026-08-03):
 *   · 한 저장이 분 경계를 넘으면 두 덩어리로 갈라진다 ......... 2건
 *   · 서로 다른 저장이 같은 분에 있으면 한 덩어리로 합쳐진다 ... 78건
 * 합쳐지는 쪽이 훨씬 많았다 — "10:30:05 저장"과 "10:30:58 저장"은 다른 저장인데
 * 한 사람이 한 번에 고친 것처럼 보였다.
 *
 * 새 규칙: 같은 사용자·같은 동작이면서 **앞 줄과 5초 이내**면 같은 저장.
 * 근거: audit_log.changed_at 은 트리거가 따로 찍지 않고 컬럼 기본값 `now()` 를 쓴다.
 *       Postgres 의 now() 는 **트랜잭션 시작 시각**이라 한 저장(한 트랜잭션)에서 나온
 *       줄들은 시각이 **완전히 같다**. 그래서 대부분은 문자열이 같은지만 봐도 되고,
 *       한 저장이 트랜잭션 여러 개로 쪼개지는 경우(실측 107건 / 10,461)만 5초가 흡수한다.
 */

import type { AuditEntry } from './types';

export interface AuditGroup {
  /** React key — 덩어리 첫 줄의 log_id (유일·안정) */
  key: string;
  action: string;
  /** 영업건 이력에 섞인 매입·출고등록 갈래 (v2.242.0 · 없으면 그 표 자체) */
  kind?: 'purchase' | 'release';
  userId: string | null;
  userName: string | null;
  /** 덩어리 대표 시각 = 첫 줄의 시각 */
  changedAt: string;
  entries: AuditEntry[];
}

/** 같은 저장으로 볼 최대 간격 (ms). 저장 한 번이 트랜잭션 여러 개로 쪼개지는 폭을 덮는다. */
const SAME_SAVE_GAP_MS = 5000;

/**
 * "2026-07-14 15:43:53.59096+09" → epoch ms.
 * ⚠공백을 'T' 로 바꾸면 안 된다 — 소수점 6자리 + '+09'(분 없음) 조합은 ISO 파서가 뱉는다.
 *   공백 형식 그대로가 오히려 파싱된다. 못 읽으면 null 을 주고, 호출부는 시각 문자열이
 *   완전히 같을 때만 묶는다(= 최소한 트랜잭션 단위는 보장).
 */
function parseTs(s: string): number | null {
  const t = new Date(s).getTime();
  return Number.isNaN(t) ? null : t;
}

/**
 * 이력 줄들을 저장 단위로 묶는다. 입력은 시각 내림차순(최신 먼저) 가정 —
 * 두 audit API 모두 `ORDER BY changed_at DESC, log_id DESC` 로 내려준다.
 */
export function groupAudit(rows: AuditEntry[]): AuditGroup[] {
  const groups: AuditGroup[] = [];
  // (사용자|동작) 별로 '지금 열려 있는 덩어리'. 다른 동작 줄이 사이에 끼어도
  // 같은 (사용자|동작) 의 바로 앞 줄과만 비교한다 — SQL 의 PARTITION BY 와 같은 뜻.
  const open = new Map<string, { g: AuditGroup; lastTs: number | null; lastRaw: string }>();

  for (const r of rows) {
    // 갈래(kind)도 나눈다 (v2.242.0) — 출고 승인 한 번에 영업건 단계·매입 연결·출고 상태가 같은 순간 같은 사람으로
    //   남는데, 한 덩어리로 묶으면 「진행단계 외 4개 변경」처럼 무엇이 바뀐 건지 안 보인다. 매입 이력 화면은 kind 가 없어 그대로다.
    const part = `${r.userId ?? ''}|${r.action}|${r.kind ?? ''}`;
    const ts = parseTs(r.changedAt);
    const cur = open.get(part);
    const sameSave =
      cur !== undefined &&
      (cur.lastRaw === r.changedAt ||
        (cur.lastTs !== null && ts !== null && Math.abs(cur.lastTs - ts) <= SAME_SAVE_GAP_MS));

    if (cur !== undefined && sameSave) {
      cur.g.entries.push(r);
      cur.lastTs = ts;
      cur.lastRaw = r.changedAt;
      continue;
    }

    const g: AuditGroup = {
      key: r.logId,
      action: r.action,
      kind: r.kind,
      userId: r.userId,
      userName: r.userName,
      changedAt: r.changedAt,
      entries: [r],
    };
    groups.push(g);
    open.set(part, { g, lastTs: ts, lastRaw: r.changedAt });
  }

  return groups;
}
